export type CampaignType = 'upgrade_plan' | 'frecuencia_premium' | 'licencia_anual' | 'storage_extra' | 'app_individual' | 'reactivacion' | 'stream_caido' | 'evento_especial';
export type CampaignStatus = 'draft' | 'scheduled' | 'active' | 'completed' | 'paused';
export type AudienceType = 'all' | 'plan' | 'status' | 'country' | 'specific';

export interface AudienceConfig {
  type: AudienceType;
  plan?: string;
  status?: string;
  country?: string;
  radioIds?: number[];
  label: string;
}

export interface Campaign {
  id: number;
  name: string;
  type: CampaignType;
  status: CampaignStatus;
  audience: AudienceConfig;
  targetCount: number;
  sentCount: number;
  openedCount: number;
  clicks: number;
  convertedCount: number;
  createdAt: string;
  scheduledAt: string | null;
  body: string;
}

export const campaignTypeLabels: Record<CampaignType, string> = {
  upgrade_plan: 'Upgrade de Plan',
  frecuencia_premium: 'Frecuencia Premium',
  licencia_anual: 'Licencia Anual',
  storage_extra: 'Storage Extra',
  app_individual: 'App Individual',
  reactivacion: 'Reactivación',
  stream_caido: 'Stream Caído',
  evento_especial: 'Evento Especial',
};

export const campaignStatusLabels: Record<CampaignStatus, string> = {
  draft: 'Borrador',
  scheduled: 'Programada',
  active: 'Activa',
  completed: 'Completada',
  paused: 'Pausada',
};

export const audienceTypeLabels: Record<AudienceType, string> = {
  all: 'Todas las radios',
  plan: 'Por plan',
  status: 'Por estado',
  country: 'Por país',
  specific: 'Radios específicas',
};

export const plans = ['Starter', 'Básico', 'Pro', 'Business', 'Premium'];
export const radioStatuses = ['active', 'pending', 'trial', 'suspended', 'stream_down'];
export const radioStatusLabels: Record<string, string> = {
  active: 'Activas',
  pending: 'Pendientes',
  trial: 'En Trial',
  suspended: 'Suspendidas',
  stream_down: 'Stream caído',
};
export const countries = ['Argentina', 'México', 'Colombia', 'Chile', 'Perú', 'España', 'Estados Unidos', 'Ecuador', 'Uruguay', 'Brasil', 'Venezuela', 'República Dominicana', 'Costa Rica', 'Paraguay', 'Bolivia'];

export const campaignTypeIcon: Record<CampaignType, string> = {
  upgrade_plan: 'ri-arrow-up-circle-line',
  frecuencia_premium: 'ri-radio-line',
  licencia_anual: 'ri-calendar-check-line',
  storage_extra: 'ri-hard-drive-2-line',
  app_individual: 'ri-smartphone-line',
  reactivacion: 'ri-restart-line',
  stream_caido: 'ri-signal-wifi-error-line',
  evento_especial: 'ri-fire-line',
};

export const campaigns: Campaign[] = [
  {
    id: 1,
    name: 'Upgrade a Pro — Oferta Junio 2026',
    type: 'upgrade_plan',
    status: 'active',
    audience: { type: 'plan', plan: 'Starter', label: 'Radios Starter (12)' },
    targetCount: 12,
    sentCount: 12,
    openedCount: 9,
    clicks: 7,
    convertedCount: 4,
    createdAt: '2026-06-01',
    scheduledAt: '2026-06-03',
    body: '¡Tu radio merece más! Actualizate a Pro por solo $19/mes y desbloqueá Lives, Fotos, Videos y Eventos ilimitados. Solo por tiempo limitado. ¡No te quedes afuera!',
  },
  {
    id: 2,
    name: 'Frecuencia Premium — Edición Limitada',
    type: 'frecuencia_premium',
    status: 'active',
    audience: { type: 'all', label: 'Todas las radios (48)' },
    targetCount: 48,
    sentCount: 48,
    openedCount: 35,
    clicks: 28,
    convertedCount: 11,
    createdAt: '2026-05-28',
    scheduledAt: '2026-05-30',
    body: 'Reservá tu frecuencia premium personalizada por tiempo limitado. Frecuencias cortas y memorables para destacar en el dial. Solo 5 frecuencias premium disponibles este mes. ¡Elegí la tuya!',
  },
  {
    id: 3,
    name: 'Licencia Anual — 25% OFF',
    type: 'licencia_anual',
    status: 'active',
    audience: { type: 'plan', plan: 'Pro', label: 'Radios Pro (18)' },
    targetCount: 18,
    sentCount: 18,
    openedCount: 14,
    clicks: 10,
    convertedCount: 6,
    createdAt: '2026-05-20',
    scheduledAt: '2026-05-22',
    body: 'Ahorrá un 25% contratando tu licencia anual. Olvidate de pagos mensuales y asegurá tu plan por todo un año. Promoción válida hasta el 30 de junio. ¡Contratá ya!',
  },
  {
    id: 4,
    name: 'Storage Extra — +50GB Gratis',
    type: 'storage_extra',
    status: 'completed',
    audience: { type: 'status', status: 'active', label: 'Radios activas (32)' },
    targetCount: 32,
    sentCount: 32,
    openedCount: 25,
    clicks: 19,
    convertedCount: 13,
    createdAt: '2026-05-10',
    scheduledAt: '2026-05-12',
    body: '¿Te quedaste sin espacio? Activá 50GB extra de storage sin costo adicional por 3 meses. Ideal para fotos, videos y grabaciones. Promoción exclusiva para radios activas. ¡Activá tu storage extra!',
  },
  {
    id: 5,
    name: 'App Individual — Radio Personal',
    type: 'app_individual',
    status: 'scheduled',
    audience: { type: 'plan', plan: 'Premium', label: 'Radios Premium (5)' },
    targetCount: 5,
    sentCount: 0,
    openedCount: 0,
    clicks: 0,
    convertedCount: 0,
    createdAt: '2026-06-05',
    scheduledAt: '2026-06-18',
    body: 'Llevá tu radio al siguiente nivel con tu propia app personalizada. Disponible en iOS y Android. Sin comisiones, 100% tuya. Precio especial de lanzamiento para radios Premium. ¡Reservá la tuya!',
  },
  {
    id: 6,
    name: 'Reactivación — Volvé con 50% OFF',
    type: 'reactivacion',
    status: 'active',
    audience: { type: 'status', status: 'suspended', label: 'Radios suspendidas (6)' },
    targetCount: 6,
    sentCount: 6,
    openedCount: 4,
    clicks: 3,
    convertedCount: 2,
    createdAt: '2026-06-02',
    scheduledAt: '2026-06-04',
    body: 'Te extrañamos. Reactivá tu radio ahora con 50% de descuento en tu primer mes de vuelta. Tu frecuencia sigue reservada. No pierdas tu lugar en el dial. ¡Volvé a sonar!',
  },
  {
    id: 7,
    name: 'Stream Caído — Recuperación Express',
    type: 'stream_caido',
    status: 'active',
    audience: { type: 'status', status: 'stream_down', label: 'Stream caído (4)' },
    targetCount: 4,
    sentCount: 4,
    openedCount: 4,
    clicks: 4,
    convertedCount: 2,
    createdAt: '2026-06-04',
    scheduledAt: null,
    body: 'Detectamos que tu stream está caído. Seguí estos 3 pasos para reactivarlo ya. Si necesitás ayuda, nuestro equipo de soporte está 24/7. ¡No pierdas oyentes!',
  },
  {
    id: 8,
    name: 'Evento Especial — Maratón Musical Julio',
    type: 'evento_especial',
    status: 'scheduled',
    audience: { type: 'country', country: 'Argentina', label: 'Argentina (15)' },
    targetCount: 15,
    sentCount: 0,
    openedCount: 0,
    clicks: 0,
    convertedCount: 0,
    createdAt: '2026-06-03',
    scheduledAt: '2026-07-01',
    body: 'Participá de la Maratón Musical de Julio. Conectá tu radio, competí por premios y ganá visibilidad en la plataforma. Evento exclusivo para radios de Argentina. ¡Inscribite ahora!',
  },
  {
    id: 9,
    name: 'Upgrade a Business — Descuento Flash',
    type: 'upgrade_plan',
    status: 'paused',
    audience: { type: 'plan', plan: 'Pro', label: 'Radios Pro (18)' },
    targetCount: 18,
    sentCount: 8,
    openedCount: 6,
    clicks: 4,
    convertedCount: 1,
    createdAt: '2026-05-15',
    scheduledAt: '2026-05-18',
    body: 'Flash Sale: Actualizate de Pro a Business con 30% OFF por 6 meses. Accedé a 100GB, API completa y moderación avanzada. Oferta por 48hs. ¡No lo pienses!',
  },
  {
    id: 10,
    name: 'Storage Extra — Plan Básico +20GB',
    type: 'storage_extra',
    status: 'draft',
    audience: { type: 'plan', plan: 'Básico', label: 'Radios Básico (8)' },
    targetCount: 8,
    sentCount: 0,
    openedCount: 0,
    clicks: 0,
    convertedCount: 0,
    createdAt: '2026-06-06',
    scheduledAt: null,
    body: 'Tu plan Básico ahora incluye +20GB de storage extra. Sin costo adicional. Empezá a subir más fotos y videos sin preocuparte. ¡Activación automática!',
  },
  {
    id: 11,
    name: 'Reactivación Free — Pasate a Pro',
    type: 'reactivacion',
    status: 'completed',
    audience: { type: 'plan', plan: 'Starter', label: 'Radios Starter inactivas (3)' },
    targetCount: 3,
    sentCount: 3,
    openedCount: 3,
    clicks: 2,
    convertedCount: 2,
    createdAt: '2026-04-20',
    scheduledAt: '2026-04-22',
    body: 'Tu radio Starter está inactiva. Reactivala y recibí 1 mes de Pro gratis. Descubrí todo lo que te estás perdiendo. ¡Actualizate y volvé al aire!',
  },
  {
    id: 12,
    name: 'Evento Especial — Noche de las Radios',
    type: 'evento_especial',
    status: 'draft',
    audience: { type: 'all', label: 'Todas las radios (48)' },
    targetCount: 48,
    sentCount: 0,
    openedCount: 0,
    clicks: 0,
    convertedCount: 0,
    createdAt: '2026-06-07',
    scheduledAt: null,
    body: 'La Noche de las Radios vuelve en agosto. Evento global con streaming en vivo, invitados especiales y premios. Todas las radios pueden participar. ¡Agendalo y preparate!',
  },
];